import { Box, Tooltip, Typography, useTheme } from '@mui/material';

import { statusLabel, statusVisual } from '../statusTokens';

const leaveBreakdown = (summary) =>
    [
        ['Casual', summary.casualLeave],
        ['Sick', summary.sickLeave],
        ['Maternity/Paternity', summary.maternityPaternityLeave],
        ['Compensatory', summary.compensatoryLeave],
        ['Other', summary.otherLeave]
    ]
        .filter(([, count]) => count > 0)
        .map(([label, count]) => `${label} ${count}`)
        .join(' · ') || 'No leaves';

const lateBreakdown = (summary) =>
    `On site late ${summary.lateOnsite || 0} · Remote late ${
        summary.lateRemote || 0
    }`;

/**
 * Count chips for a day-sheet summary (output of summarizeDays).
 * Status chips carry their status colour dot; the derived metrics
 * (Total Present, Working Days) render as plain chips. Late and Leave
 * chips expose their breakdowns as tooltips. Hides zero counts
 * (Working Days always shows).
 */
const CHIP_ITEMS = [
    { key: 'workingDays', label: 'Working Days', alwaysShow: true },
    { key: 'totalPresent', label: 'Total Present' },
    { key: 'present', status: 'in' },
    { key: 'remote', status: 'remote' },
    { key: 'arrivedLate', status: 'arrivedLate', tooltip: lateBreakdown },
    { key: 'leave', status: 'leave', tooltip: leaveBreakdown },
    { key: 'holiday', status: 'holiday' },
    { key: 'weekend', status: 'weekend' },
    { key: 'absent', status: 'absent' }
];

const AttendanceSummaryChips = ({ summary = {}, sx }) => {
    const { palette } = useTheme();

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                ...sx
            }}
        >
            {CHIP_ITEMS.map(({ key, status, label, tooltip, alwaysShow }) => {
                const count = summary[key] || 0;
                if (!count && !alwaysShow) return null;
                const visual = status ? statusVisual(palette, status) : null;

                const chip = (
                    <Box
                        key={key}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 4,
                            border: `1px solid ${palette.divider}`,
                            backgroundColor: palette.background.paper,
                            cursor: tooltip ? 'help' : 'default'
                        }}
                    >
                        {visual && (
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: visual.bg
                                }}
                            />
                        )}
                        <Typography variant='caption' color='text.secondary'>
                            {status ? statusLabel(status) : label}
                        </Typography>
                        <Typography variant='caption' sx={{ fontWeight: 700 }}>
                            {count}
                        </Typography>
                    </Box>
                );

                return tooltip ? (
                    <Tooltip key={key} title={tooltip(summary)}>
                        {chip}
                    </Tooltip>
                ) : (
                    chip
                );
            })}
        </Box>
    );
};

export { AttendanceSummaryChips as default, AttendanceSummaryChips };
